import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Lock, CheckCircle } from "lucide-react";
import { useLanguage } from "@/contexts/useLanguage";

interface SubscriptionRequiredDialogProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail?: string;
}

export const SubscriptionRequiredDialog = ({ isOpen, onClose, userEmail }: SubscriptionRequiredDialogProps) => {
  const { t } = useLanguage();

  const subscriptionPlans = [
    {
      title: t("auth.subscription.planTitle"),
      price: "€39.99",
      duration: t("auth.subscription.planDuration"),
      planType: "6-month",
      features: [
        t("auth.subscription.feature.fullAccess"),
        t("auth.subscription.feature.mockExam"),
        t("auth.subscription.feature.progressTracking"),
        t("auth.subscription.feature.studyAnalytics"),
        t("auth.subscription.feature.expertExplanations"),
        t("auth.subscription.feature.mobileAccess"),
      ],
      popular: true,
    },
  ];

  const handlePlanSelect = (planType: string) => {
    // Navigate to payment page with selected plan
    window.location.href = `/payment?plan=${planType}&email=${encodeURIComponent(userEmail || '')}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="p-3 bg-destructive/10 rounded-full">
              <Lock className="h-8 w-8 text-destructive" />
            </div>
          </div>
          <DialogTitle className="text-2xl font-bold">{t("auth.subscription.title")}</DialogTitle>
          <DialogDescription className="text-lg">
            {t("auth.subscription.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-6">
          <div className="bg-muted/50 p-4 rounded-lg border border-border">
            <div className="flex items-center gap-3">
              <CreditCard className="h-5 w-5 text-primary" />
              <div>
                <h3 className="font-semibold">{t("auth.subscription.securePayment")}</h3>
                <p className="text-sm text-muted-foreground">
                  {t("auth.subscription.choosePlan")}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            {subscriptionPlans.map((plan) => (
              <Card 
                key={plan.planType} 
                className={`relative transition-all duration-300 hover:shadow-lg ${
                  plan.popular ? 'border-primary shadow-md' : 'border-border'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge variant="default" className="bg-primary text-primary-foreground">
                      {t("auth.subscription.mostPopular")}
                    </Badge>
                  </div>
                )}
                
                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-xl">{plan.title}</CardTitle>
                  <div className="space-y-2">
                    <div className="text-3xl font-bold text-primary">{plan.price}</div>
                    <CardDescription>{t("auth.subscription.forDuration", { duration: plan.duration })}</CardDescription>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Button 
                    className="w-full mt-6" 
                    variant={plan.popular ? "default" : "outline"}
                    onClick={() => handlePlanSelect(plan.planType)}
                  >
                    {t("auth.subscription.choosePlanButton", { plan: plan.title })}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center space-y-4 pt-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              {t("auth.subscription.needHelp")}
            </p>
            <div className="flex justify-center gap-4">
              <Button variant="ghost" onClick={onClose}>
                {t("common.cancel")}
              </Button>
              <Button variant="outline" asChild>
                <a href="/contact">{t("auth.subscription.contactSupport")}</a>
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};